import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Clock3, MapPin, Phone, Wrench, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';


function formatDateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'full', timeStyle: 'short' }).format(new Date(value));
}


function toIso(value) {
  if (!value) return null;
  return new Date(value).toISOString();
}


export default function ServiceNoteResponsePage() {
  const { token } = useParams();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [choice, setChoice] = useState('');
  const [comment, setComment] = useState('');
  const [alternativeStart, setAlternativeStart] = useState('');
  const [alternativeEnd, setAlternativeEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/service-notes/public/${token}`);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'This Service Note could not be loaded.');
      }
      setNote(await response.json());
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!choice) return;
    if (choice === 'alternative_proposed' && !alternativeStart) {
      toast.error('Please enter an alternative start. / Bitte einen alternativen Beginn eingeben.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${backendUrl}/api/service-notes/public/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          response: choice,
          comment,
          alternative_start: toIso(alternativeStart),
          alternative_end: toIso(alternativeEnd),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'The response could not be sent.');
      toast.success(data.message);
      await load();
    } catch (submitError) {
      toast.error(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">Loading / Wird geladen…</div>;
  }
  if (error || !note) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-lg"><CardContent className="p-8 text-center">
          <AlertCircle className="mx-auto mb-3 h-12 w-12 text-red-500" />
          <h1 className="text-xl font-semibold">Service Note unavailable / Einsatzauftrag nicht verfügbar</h1>
          <p className="mt-2 text-slate-600">{error}</p>
        </CardContent></Card>
      </div>
    );
  }

  const responded = note.response_status && note.response_status !== 'pending';
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-4xl space-y-5">
        <Card className="overflow-hidden border-slate-200 shadow-sm">
          <CardHeader className="border-b bg-white">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                {note.company_logo_url && <img src={note.company_logo_url} alt="" className="max-h-12 max-w-[150px] object-contain" />}
                <div>
                  <p className="text-sm text-slate-500">{note.company_name}</p>
                  <CardTitle>Service Note / Einsatzauftrag {note.service_note_number}</CardTitle>
                </div>
              </div>
              <Badge>{note.response_status.replaceAll('_', ' ')}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 p-6">
            <div className="grid gap-4 rounded-lg bg-slate-50 p-4 md:grid-cols-2">
              <div><span className="text-xs uppercase text-slate-500">Project / Projekt</span><p className="font-medium">{note.project_name}</p></div>
              <div><span className="text-xs uppercase text-slate-500">Customer / Kunde</span><p className="font-medium">{note.customer_name}</p></div>
              <div className="flex gap-2"><Clock3 className="mt-0.5 h-4 w-4 text-blue-600" /><div><p>{formatDateTime(note.scheduled_start)}</p><p className="text-sm text-slate-500">to / bis {formatDateTime(note.scheduled_end)}</p></div></div>
              <div className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 text-blue-600" /><p>{note.service_location}</p></div>
              {note.customer_phone && <div className="flex gap-2"><Phone className="mt-0.5 h-4 w-4 text-blue-600" /><p>{note.customer_phone}</p></div>}
            </div>

            <section><h2 className="font-semibold">Scope of work / Arbeitsumfang</h2><p className="mt-1 whitespace-pre-wrap text-slate-700">{note.job_scope}</p></section>
            {note.tools_materials && <section><h2 className="flex items-center gap-2 font-semibold"><Wrench className="h-4 w-4" />Tools & materials / Werkzeuge & Material</h2><p className="mt-1 whitespace-pre-wrap text-slate-700">{note.tools_materials}</p></section>}
            {note.access_instructions && <section><h2 className="font-semibold">Access / Zugang</h2><p className="mt-1 whitespace-pre-wrap text-slate-700">{note.access_instructions}</p></section>}
            {note.parking_instructions && <section><h2 className="font-semibold">Parking & arrival / Parken & Anfahrt</h2><p className="mt-1 whitespace-pre-wrap text-slate-700">{note.parking_instructions}</p></section>}
            {note.safety_notes && <section><h2 className="font-semibold">Safety / Sicherheit</h2><p className="mt-1 whitespace-pre-wrap text-slate-700">{note.safety_notes}</p></section>}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            {responded ? (
              <div className="text-center">
                <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-600" />
                <h2 className="text-xl font-semibold">Response received / Rückmeldung erhalten</h2>
                <p className="mt-2 capitalize text-slate-600">{note.response_status.replaceAll('_', ' ')}</p>
                {note.alternative_start && <p className="mt-2">Suggested / Vorgeschlagen: {formatDateTime(note.alternative_start)}</p>}
                {note.response_comment && <p className="mt-2 whitespace-pre-wrap text-slate-600">{note.response_comment}</p>}
              </div>
            ) : (
              <div className="space-y-5">
                <div><h2 className="text-xl font-semibold">Can you take this job? / Können Sie diesen Auftrag übernehmen?</h2><p className="text-slate-500">One response is sent directly to the office. No login is required.</p></div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Button variant={choice === 'accepted' ? 'default' : 'outline'} onClick={() => setChoice('accepted')}><CheckCircle2 className="mr-2 h-4 w-4" />Accept / Zusagen</Button>
                  <Button variant={choice === 'declined' ? 'destructive' : 'outline'} onClick={() => setChoice('declined')}><XCircle className="mr-2 h-4 w-4" />Decline / Ablehnen</Button>
                  <Button variant={choice === 'alternative_proposed' ? 'default' : 'outline'} onClick={() => setChoice('alternative_proposed')}><Clock3 className="mr-2 h-4 w-4" />Other date / Anderer Termin</Button>
                </div>
                {choice === 'alternative_proposed' && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2"><Label>Alternative start / Alternativer Beginn *</Label><Input type="datetime-local" value={alternativeStart} onChange={(event) => setAlternativeStart(event.target.value)} /></div>
                    <div className="space-y-2"><Label>Alternative end / Alternatives Ende</Label><Input type="datetime-local" value={alternativeEnd} onChange={(event) => setAlternativeEnd(event.target.value)} /></div>
                  </div>
                )}
                {choice && <div className="space-y-2"><Label>Message / Nachricht</Label><Textarea rows={4} value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Optional details for the office / Optionale Nachricht an das Büro" /></div>}
                <Button className="w-full bg-blue-600 hover:bg-blue-700" disabled={!choice || submitting} onClick={submit}>Send response / Rückmeldung senden</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
